import styled from 'styled-components'

import React, { useEffect, useState } from 'react'

import ColorFinder from 'Utilities/ColorFinder'

import Sizes from 'Constants/Size'

import useSegment from 'Hooks/useSegment'

import Form from 'Atoms/Form'
import Svg from 'Atoms/Svg'
import Typography from 'Atoms/Typography'

const Tabs = ({
  brand,
  intent,
  items,
  activeTab,
  onChange,
  size,
  seoFocused,
  border,
  centered,
  dataTestId,
  className,
  segmentEvent,
  selectLabel,
  useDropdown = false,
}) => {
  items.forEach((item) => {
    item.key = item.key.replaceAll(' ', '_')
  })
  const [selectedTab, setSelectedTab] = useState(activeTab ? activeTab : items?.[0]?.key)
  const [eventSent, setEventSent] = useState(false)
  const { sendSegmentTrackEvent } = useSegment()

  useEffect(() => {
    if (activeTab) setSelectedTab(activeTab)
  }, [activeTab])

  const { color: hoverColor } = ColorFinder(null, 'superfluous', null, false)

  const sendEvent = () => {
    if (segmentEvent) {
      sendSegmentTrackEvent(segmentEvent)
    }
  }

  if ((!intent && !brand) || !items || Sizes.find((availableSize) => availableSize === size) === undefined) return false
  if (!Array.isArray(items) || (Array.isArray(items) && !items.length)) return false

  let tabContent = (
    <StyledTabContent className={className} role="tabpanel" id={selectedTab} aria-labelledby={`tab_${selectedTab}`}>
      {items.find((item) => item.key === selectedTab)?.content}
    </StyledTabContent>
  )

  if (seoFocused) {
    tabContent = items.map((item) => (
      <StyledTabContent
        role="tabpanel"
        aria-labelledby={`tab_${item.key}`}
        id={item.key}
        isActive={item.key === selectedTab}
        className={className}
        seoFocused={seoFocused}
        key={item.key}
      >
        {item.content}
      </StyledTabContent>
    ))
  }

  return (
    <>
      <TabWrapper isCentered={centered} useDropdown={useDropdown} role="tablist">
        {items.map((item) => {
          return (
            <TabComponent
              item={item}
              onChange={onChange}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              intent={intent}
              brand={brand}
              hoverColor={hoverColor}
              activeTab={activeTab}
              dataTestId={dataTestId}
              key={`tab_${item.key}`}
              sendEvent={sendEvent}
            />
          )
        })}
      </TabWrapper>
      {border && <TabBorder color={hoverColor} />}
      {useDropdown && (
        <Dropdown
          id={`Tabs_Select_${dataTestId}`}
          options={items.map((item) => {
            return { value: item.key, label: item.label }
          })}
          aria-label={selectLabel}
          value={selectedTab}
          font={'bold'}
          brand={'primary'}
          borderBrand={'primary'}
          displayLabel={false}
          onChange={(event) => {
            setSelectedTab(event.target.value)
            onChange ? onChange(event.target.value) : null
            if (!eventSent) {
              sendEvent()
              setEventSent(true)
            }
          }}
        />
      )}
      {tabContent}
    </>
  )
}

const TabComponent = ({
  item,
  selectedTab,
  setSelectedTab,
  onChange,
  intent,
  brand,
  hoverColor,
  activeTab,
  dataTestId,
  sendEvent,
}) => {
  const isSelected = item.key === selectedTab
  const { color: activeColor } = ColorFinder(intent, undefined, brand, false)
  const [eventSent, setEventSent] = useState(false)

  //to navigate tabs with keyboard
  useEffect(() => {
    function onKeyup(e) {
      if (e.key === 'Enter' || e.keyCode === 'Enter') {
        !activeTab ? handleOnChange(item.key) : null
        onChange ? onChange(item.key) : null
        if (!eventSent) {
          sendEvent()
          setEventSent(true)
        }
      }
    }
    const elem = document.getElementById(`tab_${item.key}`)
    if (elem) {
      document.getElementById(`tab_${item.key}`).addEventListener('keyup', onKeyup)
      const reassertedElem = document.getElementById(`tab_${item.key}`)
      if (reassertedElem) return () => reassertedElem.removeEventListener('keyup', onKeyup)
    }
  })

  const handleOnChange = (data) => {
    setSelectedTab(data)
    if (!eventSent) {
      sendEvent()
      setEventSent(true)
    }
  }

  return (
    <Tab
      isActive={isSelected}
      aria-selected={isSelected}
      activeColor={activeColor}
      role="tab"
      id={`tab_${item.key}`}
      aria-controls={item.key}
    >
      <InnerTab
        hoverColor={hoverColor}
        isSelected={isSelected}
        data-testid={`tab_${dataTestId}_${item.label}`}
        onClick={
          !isSelected
            ? () => {
                !activeTab ? handleOnChange(item.key) : null
                onChange ? onChange(item.key) : null
              }
            : () => {}
        }
      >
        <TabLabelWrapper>
          {item.icon ? (
            <TabIcon
              brand={isSelected ? brand : null}
              intent={isSelected ? intent : null}
              icon={item.icon}
              size={item.size ? item.size : 1}
            />
          ) : null}
          <Typography
            brand={isSelected ? brand : null}
            intent={isSelected ? intent : null}
            size={item.size ? item.size : 1}
            font={'bold'}
            tabIndex="0"
            style={{ whiteSpace: 'nowrap' }}
          >
            {item.label}
          </Typography>
        </TabLabelWrapper>
      </InnerTab>
    </Tab>
  )
}

const TabWrapper = styled.nav`
  display: ${(props) => (props.useDropdown ? 'none' : 'flex')};
  ${(props) => props.isCentered && 'justify-content: center;'}

  @media (min-width: 1300px) {
    ${(props) => (props.useDropdown ? 'display: flex;' : null)}
  }
`

const TabBorder = styled.div`
  border-bottom: 2px solid #d3d3d3;
  margin-top: -2px;
`

const InnerTab = styled.div`
  margin-bottom: -2px;
  padding-bottom: 4px;
  border-bottom: 2px solid transparent;
  cursor: ${(props) => (props.isSelected ? 'default' : 'pointer')};
  ${(props) => (!props.isSelected ? `&:hover {border-bottom: 2px solid ${props.hoverColor};}` : '')}
`

const Tab = styled.div`
  padding: 0 12px;
  padding-top: 6px;
  border-bottom: 2px solid ${(props) => (props.isActive ? props.activeColor : 'transparent')};
`

const TabIcon = styled(Svg)`
  margin-right: 0.25rem;
`

const TabLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledTabContent = styled.div`
  margin-top: 0.5rem;
  ${(props) => (props.seoFocused && !props.isActive ? `display: none;` : `display: block;`)}
`

const Dropdown = styled(Form.Select)`
  display: block;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  width: calc(100% - 0.5rem);
  padding: 1rem;
  height: 56px;

  @media (min-width: 1300px) {
    display: none;
  }
`

export default Tabs
